import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {Form, Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {KTCard, KTCardBody} from '../../../_dashboard/helpers'
import {AzPageHeader} from '../../components/AzPageHeader'
import {
  BlogCategory,
  createBlogCategory,
  deleteBlogCategory,
  getBlogCategories,
  updateBlogCategory,
} from './api/blogCategoriesApi'
import {useConfirm} from '../../components/ConfirmProvider'

const LANGUAGES = ['fr', 'en', 'de']

const emptyCategory = (): Omit<BlogCategory, '_id'> => ({
  slug: '',
  title: '',
  description: '',
  status: 'active',
  translations: [{language: 'fr', title: '', description: ''}],
})

const BlogCategoriesView: React.FC = () => {
  const intl = useIntl()
  const confirm = useConfirm()
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<BlogCategory[]>([])
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      const res = await getBlogCategories(0, 500)
      setCategories(Array.isArray(res?.data) ? res.data : [])
    } catch {
      toast.error(intl.formatMessage({id: 'BLOG.CATEGORY.ERROR_LOAD', defaultMessage: 'Failed to load categories'}))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const formik = useFormik({
    initialValues: emptyCategory(),
    enableReinitialize: true,
    validationSchema: Yup.object({
      title: Yup.string().required(),
      status: Yup.string().oneOf(['active', 'inactive']),
      translations: Yup.array().of(
        Yup.object({
          language: Yup.string().required(),
          title: Yup.string().required(),
        }),
      ),
    }),
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      try {
        if (editingId) {
          await updateBlogCategory(editingId, values)
          toast.success(intl.formatMessage({id: 'BLOG.CATEGORY.UPDATED', defaultMessage: 'Category updated'}))
        } else {
          await createBlogCategory(values)
          toast.success(intl.formatMessage({id: 'BLOG.CATEGORY.CREATED', defaultMessage: 'Category created'}))
        }
        resetForm()
        setShowModal(false)
        setEditingId(null)
        load()
      } catch (e: any) {
        toast.error(
          e?.response?.data?.message ||
            intl.formatMessage({id: 'BLOG.CATEGORY.ERROR_SAVE', defaultMessage: 'Failed to save category'}),
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  const openCreate = () => {
    setEditingId(null)
    formik.resetForm({values: emptyCategory()})
    setShowModal(true)
  }

  const openEdit = (cat: BlogCategory) => {
    setEditingId(cat._id || null)
    formik.setValues({
      slug: cat.slug,
      title: cat.title,
      description: cat.description || '',
      status: cat.status,
      translations: cat.translations?.length
        ? cat.translations.map((t) => ({
            language: t.language,
            title: t.title,
            description: t.description || '',
          }))
        : [{language: 'fr', title: cat.title, description: cat.description || ''}],
    })
    setShowModal(true)
  }

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      message: intl.formatMessage({id: 'BLOG.CATEGORY.CONFIRM_DELETE', defaultMessage: 'Delete category?'}),
    })
    if (!ok) return
    try {
      await deleteBlogCategory(id)
      toast.success(intl.formatMessage({id: 'BLOG.CATEGORY.DELETED', defaultMessage: 'Category deleted'}))
      load()
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          intl.formatMessage({id: 'BLOG.CATEGORY.ERROR_DELETE', defaultMessage: 'Failed to delete category'}),
      )
    }
  }

  const toggleStatus = async (cat: BlogCategory) => {
    if (!cat._id) return
    const next = cat.status === 'active' ? 'inactive' : 'active'
    try {
      await updateBlogCategory(cat._id, {status: next})
      load()
    } catch {
      toast.error(intl.formatMessage({id: 'BLOG.CATEGORY.ERROR_SAVE', defaultMessage: 'Failed to update category'}))
    }
  }

  return (
    <>
      <AzPageHeader title={intl.formatMessage({id: 'BLOG.CATEGORIES.TITLE', defaultMessage: 'Blog categories'})} />
      <KTCard>
        <KTCardBody>
          <div className='d-flex justify-content-between mb-5'>
            <Link to='/apps/blog-management' className='btn btn-light'>
              {intl.formatMessage({id: 'BLOG.BACK_TO_LIST', defaultMessage: 'Back to blogs'})}
            </Link>
            <button type='button' className='btn btn-primary' onClick={openCreate}>
              {intl.formatMessage({id: 'BLOG.CATEGORY.ADD', defaultMessage: 'Add category'})}
            </button>
          </div>

          {loading ? (
            <div className='text-center py-10'>...</div>
          ) : (
            <div className='table-responsive'>
              <table className='table align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted bg-light'>
                    <th className='ps-4'>{intl.formatMessage({id: 'BLOG.CATEGORY', defaultMessage: 'Category'})}</th>
                    <th>Slug</th>
                    <th>{intl.formatMessage({id: 'COMMON.STATUS', defaultMessage: 'Status'})}</th>
                    <th className='text-end pe-4'>{intl.formatMessage({id: 'COMMON.ACTIONS', defaultMessage: 'Actions'})}</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat._id}>
                      <td className='ps-4 fw-bold'>{cat.title}</td>
                      <td className='text-muted'>{cat.slug}</td>
                      <td>
                        <button
                          type='button'
                          className={`btn btn-sm ${cat.status === 'active' ? 'btn-success' : 'btn-secondary'}`}
                          onClick={() => toggleStatus(cat)}
                        >
                          {cat.status === 'active'
                            ? intl.formatMessage({id: 'COMMON.ACTIVE', defaultMessage: 'Active'})
                            : intl.formatMessage({id: 'COMMON.INACTIVE', defaultMessage: 'Inactive'})}
                        </button>
                      </td>
                      <td className='text-end pe-4'>
                        <button type='button' className='btn btn-sm btn-light-primary me-2' onClick={() => openEdit(cat)}>
                          {intl.formatMessage({id: 'ACTION.EDIT', defaultMessage: 'Edit'})}
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger'
                          onClick={() => cat._id && handleDelete(cat._id)}
                        >
                          {intl.formatMessage({id: 'ACTION.DELETE', defaultMessage: 'Delete'})}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </KTCardBody>
      </KTCard>

      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingId
                ? intl.formatMessage({id: 'BLOG.CATEGORY.EDIT', defaultMessage: 'Edit category'})
                : intl.formatMessage({id: 'BLOG.CATEGORY.ADD', defaultMessage: 'Add category'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='d-flex flex-column gap-4'>
            <div>
              <label className='form-label required'>
                {intl.formatMessage({id: 'BLOG.CATEGORY.DEFAULT_TITLE', defaultMessage: 'Default title'})}
              </label>
              <input className='form-control' {...formik.getFieldProps('title')} />
            </div>
            <div>
              <label className='form-label'>
                {intl.formatMessage({id: 'BLOG.CATEGORY.SLUG', defaultMessage: 'Slug (optional)'})}
              </label>
              <input className='form-control' {...formik.getFieldProps('slug')} />
            </div>
            <div>
              <label className='form-label'>
                {intl.formatMessage({id: 'COMMON.DESCRIPTION', defaultMessage: 'Description'})}
              </label>
              <textarea className='form-control' rows={2} {...formik.getFieldProps('description')} />
            </div>
            <div>
              <label className='form-label'>{intl.formatMessage({id: 'COMMON.STATUS', defaultMessage: 'Status'})}</label>
              <select className='form-select' {...formik.getFieldProps('status')}>
                <option value='active'>{intl.formatMessage({id: 'COMMON.ACTIVE', defaultMessage: 'Active'})}</option>
                <option value='inactive'>{intl.formatMessage({id: 'COMMON.INACTIVE', defaultMessage: 'Inactive'})}</option>
              </select>
            </div>
            <div>
              <h5 className='mb-3'>{intl.formatMessage({id: 'BLOG.CATEGORY.TRANSLATIONS', defaultMessage: 'Translations'})}</h5>
              {(formik.values.translations || []).map((tr, idx) => (
                <div key={idx} className='border rounded p-3 mb-3'>
                  <div className='row g-3'>
                    <div className='col-md-3'>
                      <label className='form-label'>Lang</label>
                      <select
                        className='form-select'
                        value={tr.language}
                        onChange={(e) => formik.setFieldValue(`translations[${idx}].language`, e.target.value)}
                      >
                        {LANGUAGES.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-md-9'>
                      <label className='form-label required'>Title</label>
                      <input
                        className='form-control'
                        value={tr.title}
                        onChange={(e) => formik.setFieldValue(`translations[${idx}].title`, e.target.value)}
                      />
                    </div>
                    <div className='col-12'>
                      <label className='form-label'>Description</label>
                      <textarea
                        className='form-control'
                        rows={2}
                        value={tr.description || ''}
                        onChange={(e) => formik.setFieldValue(`translations[${idx}].description`, e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() =>
                  formik.setFieldValue('translations', [
                    ...(formik.values.translations || []),
                    {language: 'en', title: '', description: ''},
                  ])
                }
              >
                + {intl.formatMessage({id: 'BLOG.CATEGORY.ADD_TRANSLATION', defaultMessage: 'Add translation'})}
              </button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type='button' className='btn btn-light' onClick={() => setShowModal(false)}>
              {intl.formatMessage({id: 'ACTION.CANCEL', defaultMessage: 'Cancel'})}
            </button>
            <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
              {intl.formatMessage({id: 'ACTION.SAVE', defaultMessage: 'Save'})}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default BlogCategoriesView
